import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  List,
  Typography,
  Space,
  Tag,
  Spin,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  SettingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { tenantApi, TenantDocTypeConfig, Tenant } from '../services/api/tenant';
import { mappingApi } from '../services/api/mapping';

const { Title, Text } = Typography;

export function TenantDocTypes() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [configs, setConfigs] = useState<TenantDocTypeConfig[]>([]);
  const [availableDocTypes, setAvailableDocTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantResponse, configsResponse, docTypesResponse] =
        await Promise.all([
          tenantApi.get(tenantId!),
          tenantApi.listDocTypes(tenantId!),
          mappingApi.listDocTypes(),
        ]);

      setTenant(tenantResponse.data);
      setConfigs(configsResponse.data.data);
      setAvailableDocTypes(docTypesResponse.data);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const configuredDocTypes = new Set(configs.map((c) => c.docType));
  const unconfiguredDocTypes = availableDocTypes.filter(
    (dt) => !configuredDocTypes.has(dt.id)
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div style={{ padding: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/tenants')}
          style={{ marginBottom: 16 }}
        >
          Back to Tenants
        </Button>
        <Alert
          message="Error"
          description={error || 'Tenant not found'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/tenants')}
            style={{ marginBottom: 16 }}
          >
            Back to Tenants
          </Button>
          <Title level={2}>{tenant.name} - DocType Configurations</Title>
        </div>

        <Card title="Configured DocTypes">
          {configs.length === 0 ? (
            <Text type="secondary">
              No DocType configurations yet. Configure a DocType below to get
              started.
            </Text>
          ) : (
            <List
              dataSource={configs}
              renderItem={(config) => (
                <List.Item
                  actions={[
                    <Button
                      key="configure"
                      icon={<SettingOutlined />}
                      onClick={() =>
                        navigate(
                          `/tenants/${tenantId}/doctype/${config.docType}`
                        )
                      }
                    >
                      Configure Fields
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={config.docType.toUpperCase()}
                    description={
                      <Space>
                        <Text type="secondary">
                          {config.fields.length} field(s) configured
                        </Text>
                        {config.updatedAt && (
                          <Text type="secondary">
                            • Last updated:{' '}
                            {new Date(config.updatedAt).toLocaleString()}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        <Card title="Available DocTypes">
          {unconfiguredDocTypes.length === 0 ? (
            <Text type="secondary">
              All available DocTypes have been configured.
            </Text>
          ) : (
            <List
              dataSource={unconfiguredDocTypes}
              renderItem={(docType) => (
                <List.Item
                  actions={[
                    <Button
                      key="add"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        navigate(`/tenants/${tenantId}/doctype/${docType.id}`)
                      }
                    >
                      Configure
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {docType.name}
                        <Tag color="blue">{docType.id}</Tag>
                      </Space>
                    }
                    description="Not configured"
                  />
                </List.Item>
              )}
            />
          )}
        </Card>
      </Space>
    </div>
  );
}